import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Filter, Calendar as CalendarIcon, TrendingUp, AlertCircle, Bell, BellOff, RefreshCw, Clock, ChevronDown, ChevronUp, X, Phone, Mail, Settings, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAuth, API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import TeamMatchup from "../components/TeamMatchup";
import HotStreaks from "../components/HotStreaks";
import axios from "axios";
import SEO from "../components/SEO";
import { fetchWithRetry } from "../utils/apiService";
import { SituationalTagList } from "../components/SituationalTags";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";

const SPORTS = ["All", "NFL", "NBA", "WNBA", "MLB", "NHL", "Soccer", "UFC", "NCAAF", "NCAAB", "Tennis", "PGA"];

// Helper to calculate profit based on American odds
const calculatePickProfit = (pick) => {
  if (pick.result !== 'win' && pick.result !== 'loss') return 0;
  
  const betAmount = pick.bet_amount || 100;
  const oddsNum = parseInt(String(pick.odds || '-110').replace('+', ''));
  
  if (pick.result === 'win') {
    if (oddsNum > 0) {
      return betAmount * (oddsNum / 100);
    } else {
      return betAmount * (100 / Math.abs(oddsNum));
    }
  }
  return -betAmount;
};

// Format profit display
const formatProfit = (profit) => {
  if (profit >= 0) {
    return `+$${profit.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
  }
  return `-$${Math.abs(profit).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
};

// Recent Stats Bar Component with expandable picks
const RecentStatsBar = ({ stats, API }) => {
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [periodPicks, setPeriodPicks] = useState([]);
  const [loadingPicks, setLoadingPicks] = useState(false);

  const fetchPeriodPicks = async (period) => {
    if (expandedPeriod === period) {
      setExpandedPeriod(null);
      return;
    }
    
    setLoadingPicks(true);
    setExpandedPeriod(period);
    
    try {
      const response = await axios.get(`${API}/stats/recent-picks`, {
        params: { period }
      });
      setPeriodPicks(response.data.picks || []);
    } catch (error) {
      console.error('Failed to fetch period picks:', error);
      setPeriodPicks([]);
    } finally {
      setLoadingPicks(false);
    }
  };

  if (!stats) {
    return (
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-hsl-surface/50 rounded-lg p-3 animate-pulse">
            <div className="h-4 bg-zinc-700 rounded w-20 mb-2"></div>
            <div className="h-6 bg-zinc-700 rounded w-24"></div>
          </div>
        ))}
      </div>
    );
  }

  const StatBox = ({ label, data, period }) => {
    const record = `${data.wins}-${data.losses}`;
    const profit = data.profit;
    const hasGames = data.wins > 0 || data.losses > 0;
    const isExpanded = expandedPeriod === period;
    
    return (
      <button
        onClick={() => hasGames && fetchPeriodPicks(period)}
        disabled={!hasGames}
        className={`bg-hsl-surface/50 border rounded-lg p-3 text-center transition-all w-full ${
          hasGames 
            ? 'border-white/5 hover:border-hsl-primary/50 hover:bg-hsl-surface cursor-pointer' 
            : 'border-white/5 cursor-default'
        } ${isExpanded ? 'border-hsl-primary bg-hsl-surface' : ''}`}
      >
        <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{label}</p>
        {hasGames ? (
          <>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-lg text-white font-bold">{record}</span>
              <span className={`font-mono text-lg font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {profit >= 0 ? '+' : ''}${profit.toLocaleString()}
              </span>
            </div>
            <p className="text-cyan-400 text-xs mt-1 flex items-center justify-center gap-1">
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {isExpanded ? 'Hide picks' : 'View picks'}
            </p>
          </>
        ) : (
          <p className="text-zinc-600 text-sm">No picks</p>
        )}
      </button>
    );
  };

  const periodLabels = {
    yesterday: 'Yesterday',
    last_7_days: 'Last 7 Days',
    last_30_days: 'Last 30 Days'
  };

  return (
    <div className="mb-6" data-testid="recent-stats-bar">
      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Yesterday" data={stats.yesterday} period="yesterday" />
        <StatBox label="Last 7 Days" data={stats.last_7_days} period="last_7_days" />
        <StatBox label="Last 30 Days" data={stats.last_30_days} period="last_30_days" />
      </div>
      
      {/* Expanded Picks Section */}
      <AnimatePresence>
        {expandedPeriod && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 bg-hsl-surface/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold uppercase text-white">
                  {periodLabels[expandedPeriod]} Picks
                </h3>
                <button
                  onClick={() => setExpandedPeriod(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {loadingPicks ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-hsl-bg/50 rounded-lg p-4 animate-pulse">
                      <div className="h-4 bg-zinc-700 rounded w-32 mb-2"></div>
                      <div className="h-6 bg-zinc-700 rounded w-48 mb-2"></div>
                      <div className="h-12 bg-zinc-700 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              ) : periodPicks.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">No picks found for this period</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {periodPicks.map((pick) => (
                    <motion.div
                      key={pick.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-hsl-bg/50 border rounded-lg p-4 ${
                        pick.result === 'win' 
                          ? 'border-green-500/30' 
                          : pick.result === 'loss'
                          ? 'border-red-500/30'
                          : 'border-zinc-700/50'
                      }`}
                    >
                      {/* Pick Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                            pick.result === 'win' 
                              ? 'bg-green-500/20 text-green-400' 
                              : pick.result === 'loss'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {pick.result}
                          </span>
                          <span className="text-zinc-500 text-xs">{pick.sport}</span>
                          {pick.pick_tier === 'hammer' && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded font-bold">
                              🔨 HAMMER
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-bold ${
                            pick.result === 'win' ? 'text-green-400' : pick.result === 'loss' ? 'text-red-400' : 'text-zinc-400'
                          }`}>
                            {formatProfit(calculatePickProfit(pick))}
                          </p>
                          <p className="text-zinc-500 text-xs">
                            {new Date(pick.game_time).toLocaleDateString('en-US', { 
                              timeZone: 'America/New_York',
                              month: 'short', 
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      
                      {/* Matchup */}
                      <h4 className="font-heading font-bold text-white uppercase text-base mb-1">
                        <TeamMatchup matchup={pick.matchup} sport={pick.sport} compact />
                      </h4>
                      
                      {/* Pick & Odds */}
                      <p className="text-cyan-400 font-semibold text-sm mb-3">
                        {pick.pick} <span className="text-zinc-500">({pick.odds})</span>
                      </p>
                      
                      {/* Analysis/Writeup */}
                      {pick.analysis && (
                        <div className="bg-hsl-bg/80 rounded-lg p-3 border border-white/5">
                          <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-wrap">
                            {pick.analysis}
                          </p>
                        </div>
                      )}
                      
                      {/* Score if available */}
                      {(pick.away_score !== null && pick.home_score !== null) && (
                        <p className="text-zinc-500 text-xs mt-2">
                          Final Score: <span className="text-white font-mono">{pick.away_score} - {pick.home_score}</span>
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Animated Hammer graphic component
const HammerGraphic = () => (
  <motion.div 
    className="absolute -top-2 -right-2 z-20"
    initial={{ rotate: -20 }}
    animate={{ rotate: [-20, 0, -20] }}
    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
  >
    <div className="relative">
      <div className="absolute inset-0 bg-yellow-400/30 blur-xl rounded-full" />
      <div className="relative bg-gradient-to-br from-yellow-400 to-orange-500 text-black px-3 py-2 rounded-lg shadow-lg shadow-yellow-500/30 font-heading font-black text-sm uppercase tracking-tight flex items-center gap-1">
        <span className="text-lg">🔨</span>
        <span>DROP IT</span>
      </div>
    </div>
  </motion.div>
);

const PickCard = ({ pick, isPremiumUser, isFreePhase = true, tagStats = {} }) => {
  // Premium users get full access, free users see completed picks only
  const isLocked = !isFreePhase && (pick.is_premium || pick.pick_tier === 'premium' || pick.pick_tier === 'hammer') && !isPremiumUser;
  const isHammer = pick.pick_tier === 'hammer';
  const liveScore = pick.live_score;
  
  // Determine if game has started (compare game_time to now)
  const gameTime = new Date(pick.game_time);
  const now = new Date();
  const gameHasStarted = gameTime <= now;
  
  // Check if pick is already graded (not pending)
  const isGraded = pick.result && pick.result !== 'pending';
  
  // Check for manually entered final scores
  const hasManualScore = pick.away_score !== null && pick.away_score !== undefined && 
                         pick.home_score !== null && pick.home_score !== undefined;
  
  // Show score section if: 
  // 1. Manual scores exist, OR
  // 2. Live score exists AND game has started AND pick is NOT yet graded
  // Don't show stale "LIVE" badges for already graded picks unless we have actual scores
  const showScore = hasManualScore || 
    (liveScore && gameHasStarted && !isGraded && (liveScore.home_score !== null || liveScore.away_score !== null));
  
  // Determine which scores to display (prefer manual over live)
  // If graded and has manual scores, show as FINAL
  // If not graded and has live scores, show as LIVE
  const displayScore = hasManualScore 
    ? { away: pick.away_score, home: pick.home_score, isManual: true, completed: true }
    : (liveScore && !isGraded)
      ? { away: liveScore.team1_score, home: liveScore.team2_score, isManual: false, completed: liveScore.completed }
      : null;
  
  const getResultBadge = (result) => {
    switch (result) {
      case "win":
        return <span className="badge-win">WIN</span>;
      case "loss":
        return <span className="badge-loss">LOSS</span>;
      case "push":
        return <span className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 px-2 py-1 rounded text-xs font-bold uppercase">PUSH</span>;
      default:
        return <span className="badge-pending">PENDING</span>;
    }
  };

  const formatOdds = (odds) => {
    const num = parseInt(odds);
    return num > 0 ? `+${odds}` : odds;
  };

  // Calculate CLV (Closing Line Value)
  const calculateClv = () => {
    if (!pick.closing_line) return null;
    const parseOdds = (oddsStr) => {
      const str = String(oddsStr).trim();
      if (str.startsWith('+')) return parseInt(str.slice(1));
      return parseInt(str);
    };
    const our = parseOdds(pick.odds);
    const close = parseOdds(pick.closing_line);
    if (isNaN(our) || isNaN(close)) return null;
    
    // Calculate CLV based on odds movement
    if (our < 0 && close < 0) {
      return Math.abs(close) - Math.abs(our);
    } else if (our > 0 && close > 0) {
      return our - close;
    } else if (our < 0 && close > 0) {
      return Math.abs(our) + close;
    } else if (our > 0 && close < 0) {
      return our + Math.abs(close);
    }
    return 0;
  };

  const clv = calculateClv();

  const getTierBadge = () => {
    if (isHammer) {
      return (
        <motion.span 
          className="bg-gradient-to-r from-yellow-500/30 to-orange-500/30 text-yellow-300 border border-yellow-500/50 px-3 py-1 rounded text-xs font-bold uppercase shadow-lg shadow-yellow-500/20"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          🔨 HAMMER
        </motion.span>
      );
    }
    if (pick.pick_tier === 'scheduled_loss') {
      return (
        <span className="bg-purple-500/20 text-purple-300 border border-purple-500/40 px-3 py-1 rounded text-xs font-bold uppercase">
          📅 SCHEDULED LOSS
        </span>
      );
    }
    if (pick.pick_tier === 'premium' || pick.is_premium) {
      return <span className="badge-premium">MEMBERS</span>;
    }
    return null;
  };

  const isScheduledLoss = pick.pick_tier === 'scheduled_loss';
  const isWin = pick.result === 'win';
  const isLoss = pick.result === 'loss';

  // Determine card styling based on result
  const getCardClasses = () => {
    let classes = 'pick-card p-6 relative';
    
    if (isLocked) classes += ' overflow-hidden';
    
    // Result-based styling (takes priority for completed picks)
    if (isWin) {
      classes += ' ring-2 ring-green-500/60 bg-gradient-to-br from-hsl-surface to-green-950/30';
    } else if (isLoss) {
      classes += ' ring-2 ring-red-500/50 bg-gradient-to-br from-hsl-surface to-red-950/20';
    } else if (isHammer) {
      // Hammer styling (only if not graded)
      classes += ' ring-2 ring-yellow-500/50 bg-gradient-to-br from-hsl-surface to-yellow-950/20';
    } else if (isScheduledLoss) {
      // Scheduled loss styling (only if not graded)
      classes += ' ring-2 ring-purple-500/50 bg-gradient-to-br from-hsl-surface to-purple-950/20';
    }
    
    return classes;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={getCardClasses()}
      data-testid={`pick-card-${pick.id}`}
    >
      {/* Hammer graphic for HAMMER picks */}
      {isHammer && !isLocked && <HammerGraphic />}
      
      {isLocked && (
        <div className="absolute inset-0 bg-hsl-bg/90 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="text-center">
            <Lock className="w-8 h-8 text-hsl-primary mx-auto mb-2" />
            <p className="text-white font-heading font-bold uppercase">
              {isHammer ? '🔨 HAMMER Pick' : 'Members Only'}
            </p>
            <Link to="/pricing">
              <Button className="mt-2 btn-primary-glow px-4 py-2 text-sm rounded-sm" data-testid="unlock-pick-btn">
                Unlock Now
              </Button>
            </Link>
          </div>
        </div>
      )}
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-hsl-primary font-heading font-bold uppercase text-sm tracking-wider">
              {pick.league}
            </span>
            {getTierBadge()}
            {getResultBadge(pick.result)}
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-white uppercase tracking-tight mb-1" data-testid="pick-matchup">
            <TeamMatchup matchup={pick.matchup} sport={pick.sport || pick.league} />
          </h3>
          
          {/* Score Display - Manual scores or Live scores */}
          {showScore && displayScore && (
            <div className="flex items-center gap-2 mb-2">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                displayScore.isManual || displayScore.completed 
                  ? 'bg-zinc-800/80' 
                  : 'bg-green-500/20 border border-green-500/30'
              }`}>
                {!displayScore.isManual && !displayScore.completed && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                )}
                <span className={`font-mono font-bold text-lg ${
                  displayScore.isManual || displayScore.completed 
                    ? 'text-zinc-300' 
                    : 'text-green-400'
                }`}>
                  {displayScore.away ?? '-'} - {displayScore.home ?? '-'}
                </span>
                <span className={`text-xs font-semibold uppercase ${
                  displayScore.isManual || displayScore.completed 
                    ? 'text-zinc-500' 
                    : 'text-green-400'
                }`}>
                  {displayScore.isManual ? 'FINAL' : (displayScore.completed ? 'FINAL' : 'LIVE')}
                </span>
              </div>
            </div>
          )}
          
          <p className="text-zinc-400 text-sm mb-3" data-testid="pick-game-time">
            {new Date(pick.game_time).toLocaleDateString('en-US', { 
              timeZone: 'America/New_York',
              weekday: 'short', 
              month: 'short', 
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })} ET
          </p>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">Pick</p>
            <p className="font-heading font-bold text-xl text-white" data-testid="pick-selection">
              {isLocked ? "***" : pick.pick}
            </p>
          </div>
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">Odds</p>
            <p className={`font-mono font-bold text-xl ${parseInt(pick.odds) > 0 ? 'text-green-400' : 'text-white'}`} data-testid="pick-odds">
              {isLocked ? "***" : formatOdds(pick.odds)}
            </p>
            {/* CLV Display */}
            {!isLocked && clv !== null && (
              <div className="mt-1">
                <span className={`text-xs font-semibold ${clv > 0 ? 'text-green-400' : clv < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                  CLV: {clv > 0 ? '+' : ''}{clv}
                </span>
              </div>
            )}
          </div>
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">Bet</p>
            <p className="font-mono font-bold text-xl text-hsl-primary" data-testid="pick-bet">
              {isLocked ? "***" : `$${pick.bet_amount}`}
            </p>
          </div>
        </div>
      </div>
      
      {!isLocked && (
        <div className={`mt-4 pt-4 border-t ${isWin ? 'border-green-500/20' : isLoss ? 'border-red-500/20' : 'border-white/5'}`}>
          {/* Situational Edge Tags */}
          {pick.situational_tags && pick.situational_tags.length > 0 && (
            <div className="mb-3">
              <SituationalTagList tags={pick.situational_tags} size="sm" tagStats={tagStats} />
            </div>
          )}
          <p className={`text-sm ${isLoss ? 'text-red-300/80' : 'text-zinc-400'}`} data-testid="pick-analysis">
            <span className={`font-semibold ${isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-hsl-primary'}`}>Analysis: </span>
            {pick.analysis}
          </p>
        </div>
      )}
    </motion.div>
  );
};

// Notification Toggle Component
const NotificationToggle = () => {
  const { user, token, updateNotifications } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  
  // Check push notification status on mount
  useEffect(() => {
    const checkPush = async () => {
      const { isPushSupported, isSubscribed } = await import('../utils/pushNotifications');
      setPushSupported(isPushSupported());
      if (isPushSupported()) {
        const subscribed = await isSubscribed();
        setPushEnabled(subscribed);
      }
    };
    checkPush();
  }, []);
  
  const handleEmailToggle = async () => {
    setIsUpdating(true);
    try {
      await updateNotifications({ email_notifications: !user?.email_notifications });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSmsToggle = async () => {
    if (!user?.sms_notifications && !phoneNumber) {
      setShowSettings(true);
      return;
    }
    setIsUpdating(true);
    try {
      await updateNotifications({ sms_notifications: !user?.sms_notifications });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSavePhone = async () => {
    setIsUpdating(true);
    try {
      await updateNotifications({ 
        phone_number: phoneNumber,
        sms_notifications: true 
      });
      setShowSettings(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePushToggle = async () => {
    if (!token) return;
    setIsUpdating(true);
    try {
      const { subscribeToPush, unsubscribeFromPush } = await import('../utils/pushNotifications');
      const { toast } = await import('sonner');
      
      if (pushEnabled) {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        await subscribeToPush(token);
        setPushEnabled(true);
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      const { toast } = await import('sonner');
      if (error.message.includes('denied')) {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error(error.message || 'Failed to update push notifications');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDigestChange = async (preference) => {
    setIsUpdating(true);
    try {
      await updateNotifications({ digest_preference: preference });
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <div className="bg-hsl-surface border border-white/10 rounded-xl p-4 mb-6" data-testid="notification-toggle-banner">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-hsl-primary" />
          <div>
            <p className="text-white font-medium text-sm">Pick Notifications</p>
            <p className="text-zinc-500 text-xs">Get notified when new picks drop</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
          className="text-zinc-400 hover:text-white"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </div>

      {/* Quick toggles */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-zinc-500" />
          <span className="text-sm text-zinc-400">Email</span>
          <Switch
            checked={user?.email_notifications || false}
            onCheckedChange={handleEmailToggle}
            disabled={isUpdating}
            data-testid="email-notification-switch"
          />
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-zinc-500" />
          <span className="text-sm text-zinc-400">SMS</span>
          <Switch
            checked={user?.sms_notifications || false}
            onCheckedChange={handleSmsToggle}
            disabled={isUpdating}
            data-testid="sms-notification-switch"
          />
        </div>
        {pushSupported && (
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-400">Push</span>
            <Switch
              checked={pushEnabled}
              onCheckedChange={handlePushToggle}
              disabled={isUpdating}
              data-testid="push-notification-switch"
            />
          </div>
        )}
      </div>

      {/* Expanded settings */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
              {/* Phone number input */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Phone Number (for SMS)</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="bg-hsl-bg border-white/10 text-white"
                  />
                  <Button 
                    onClick={handleSavePhone} 
                    disabled={isUpdating || !phoneNumber}
                    size="sm"
                  >
                    Save
                  </Button>
                </div>
              </div>

              {/* Digest preferences */}
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Email Digest</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'none', label: 'None' },
                    { value: 'daily', label: 'Daily Recap' },
                    { value: 'weekly', label: 'Weekly Summary' }
                  ].map((option) => (
                    <Button
                      key={option.value}
                      variant={user?.digest_preference === option.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDigestChange(option.value)}
                      disabled={isUpdating}
                      className={user?.digest_preference === option.value 
                        ? "bg-hsl-primary text-black" 
                        : "border-white/20 text-zinc-400 hover:text-white"
                      }
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {user?.digest_preference === 'daily' && "You'll receive a recap every morning at 9 AM ET"}
                  {user?.digest_preference === 'weekly' && "You'll receive a summary every Monday at 9 AM ET"}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState("All");
  
  // Get initial filter from URL params, default based on login status
  // Non-logged in users default to "completed", logged-in users to "active"
  const urlFilter = searchParams.get("filter");
  const getInitialFilter = () => {
    if (urlFilter === "active") return "active";
    if (urlFilter === "completed") return "completed";
    if (urlFilter === "all") return "all";
    // Default: logged-in users see active, guests see completed
    return user ? "active" : "completed";
  };
  const [statusFilter, setStatusFilter] = useState(getInitialFilter());
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePreset, setDatePreset] = useState("all"); // "all", "7d", "30d", "thisMonth", "lastMonth", "custom"
  
  const [trackRecord, setTrackRecord] = useState(null);
  const [recentStats, setRecentStats] = useState(null);
  const [tagStats, setTagStats] = useState({});
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const isPremiumUser = user?.subscription_tier === "premium" || user?.subscription_tier === "vip" || user?.is_admin;

  // Fetch tag stats once on mount
  useEffect(() => {
    const fetchTagStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/situational-tags`, { timeout: 15000 });
        // Convert array to object keyed by tag code for easy lookup
        const statsMap = {};
        (response.data.tags || []).forEach(tag => {
          statsMap[tag.code] = tag;
        });
        setTagStats(statsMap);
      } catch (error) {
        console.warn('Failed to fetch tag stats:', error.message);
      }
    };
    fetchTagStats();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { limit: 1000 };
        
        // Handle favorites filter - fetch all and filter client-side
        if (selectedSport === "favorites" && user?.favorite_sports?.length > 0) {
          // Don't send sport param, we'll filter client-side
        } else if (selectedSport !== "All") {
          params.sport = selectedSport;
        }
        
        if (statusFilter !== "all") params.status = statusFilter;
        
        // Add date range parameters
        if (dateRange.from) {
          params.start_date = format(dateRange.from, "yyyy-MM-dd");
        }
        if (dateRange.to) {
          params.end_date = format(dateRange.to, "yyyy-MM-dd");
        }
        
        // Use the global fetchWithRetry for robust connection handling
        const [picksRes, statsRes, recentRes] = await Promise.all([
          fetchWithRetry(`${API}/picks`, { params }),
          fetchWithRetry(`${API}/track-record`),
          fetchWithRetry(`${API}/stats/recent`)
        ]);
        
        // Filter by favorites if needed
        let filteredPicks = picksRes.data;
        if (selectedSport === "favorites" && user?.favorite_sports?.length > 0) {
          filteredPicks = filteredPicks.filter(pick => user.favorite_sports.includes(pick.sport));
        }
        
        setPicks(filteredPicks);
        setTrackRecord(statsRes.data);
        setRecentStats(recentRes.data);
      } catch (error) {
        console.warn('DashboardPage: Failed to load data after retries:', error.message);
        // Try to at least get picks with extended timeout
        try {
          const params = { limit: 1000 };
          if (selectedSport !== "All" && selectedSport !== "favorites") params.sport = selectedSport;
          if (statusFilter !== "all") params.status = statusFilter;
          if (dateRange.from) params.start_date = format(dateRange.from, "yyyy-MM-dd");
          if (dateRange.to) params.end_date = format(dateRange.to, "yyyy-MM-dd");
          const picksRes = await axios.get(`${API}/picks`, { params, timeout: 30000 });
          
          let filteredPicks = picksRes.data;
          if (selectedSport === "favorites" && user?.favorite_sports?.length > 0) {
            filteredPicks = filteredPicks.filter(pick => user.favorite_sports.includes(pick.sport));
          }
          setPicks(filteredPicks);
        } catch (e) {
          console.warn('DashboardPage: Failed to load picks:', e.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport, statusFilter, dateRange, user?.favorite_sports]);

  // Toggle favorite sport
  const toggleFavorite = async (sport) => {
    if (!user || !token) return;
    
    const currentFavorites = user.favorite_sports || [];
    const newFavorites = currentFavorites.includes(sport)
      ? currentFavorites.filter(s => s !== sport)
      : [...currentFavorites, sport];
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/auth/notifications`, 
        { favorite_sports: newFavorites },
        { headers }
      );
      // Update local user state (assuming useAuth provides a way to update user)
      // For now, just show a toast
      const { toast } = await import("sonner");
      if (newFavorites.includes(sport)) {
        toast.success(`${sport} added to favorites`);
      } else {
        toast.success(`${sport} removed from favorites`);
      }
      // Force reload to get updated user data
      window.location.reload();
    } catch (error) {
      console.warn('Failed to update favorites:', error.message);
    }
  };

  // Manual refresh function - just refetches picks, no API score calls
  const [refreshing, setRefreshing] = useState(false);
  const refreshPicks = async () => {
    setRefreshing(true);
    try {
      const params = { limit: 1000 };
      if (selectedSport !== "All") params.sport = selectedSport;
      if (statusFilter !== "all") params.status = statusFilter;
      
      const picksRes = await axios.get(`${API}/picks`, { params });
      setPicks(picksRes.data);
    } catch (error) {
      console.warn('DashboardPage: Failed to refresh picks:', error.message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="dashboard-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      <SEO 
        title="Today's Sports Picks"
        description="Get today's expert sports picks with detailed analysis. Premium picks with 7-day free trial. NFL, NBA, MLB, NHL, and more."
        keywords="today's sports picks, daily picks, free sports picks, expert analysis, betting picks"
        canonical="/dashboard"
      />
      
      {/* Header */}
      <div className="border-b border-white/5 bg-hsl-surface/50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Premium/Trial Promotion Banner - only show to non-premium users */}
          {user && user.subscription_tier !== "premium" && user.subscription_tier !== "vip" && (
            <div className="bg-gradient-to-r from-hsl-primary/10 to-cyan-500/10 border border-hsl-primary/30 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎁</span>
                <div>
                  <p className="text-hsl-primary font-bold text-sm uppercase tracking-wide">Try HSL Premium Free for 7 Days!</p>
                  <p className="text-hsl-primary/60 text-xs">Use code HSLTRIAL at checkout - no credit card required</p>
                </div>
              </div>
              <Link to="/pricing" className="text-hsl-primary text-xs hover:underline font-semibold">Start Free Trial →</Link>
            </div>
          )}
          
          {/* Recent Performance Stats */}
          <RecentStatsBar stats={recentStats} API={API} />
          
          {/* Hot Streaks */}
          <HotStreaks compact className="mb-6" />
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white" data-testid="dashboard-title">
                Today's Picks
              </h1>
              <p className="text-zinc-400 mt-2">Expert analysis for smart betting</p>
            </div>
            
            {trackRecord && (
              <div className="flex gap-4 md:gap-8">
                <div className="text-center">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Season Record</p>
                  <p className="font-mono text-xl text-white font-bold" data-testid="dashboard-record">
                    {trackRecord.wins}-{trackRecord.losses}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Win Rate</p>
                  <p className="font-mono text-xl text-hsl-primary font-bold" data-testid="dashboard-win-rate">
                    {trackRecord.win_rate}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Profit</p>
                  <p className={`font-mono text-xl font-bold ${trackRecord.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="dashboard-profit">
                    {trackRecord.total_profit >= 0 ? '+' : '-'}${Math.abs(trackRecord.total_profit).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sport Filters */}
      {/* Date & Sport Filters */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={18} className="text-zinc-500" />
              <button
                onClick={() => setStatusFilter("active")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  statusFilter === "active" 
                    ? 'bg-hsl-primary text-black' 
                    : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-active"
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter("completed")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  statusFilter === "completed" 
                    ? 'bg-hsl-primary text-black' 
                    : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-completed"
              >
                Completed
              </button>
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  statusFilter === "all" 
                    ? 'bg-hsl-primary text-black' 
                    : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-all"
              >
                All Picks ({trackRecord?.total_picks || 0})
              </button>
            </div>
            
            {/* Date Range Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Clock size={18} className="text-zinc-500" />
              <button
                onClick={() => { setDatePreset("all"); setDateRange({ from: null, to: null }); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  datePreset === "all" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
              >
                All Time
              </button>
              <button
                onClick={() => { 
                  setDatePreset("7d"); 
                  setDateRange({ from: subDays(new Date(), 7), to: new Date() }); 
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  datePreset === "7d" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => { 
                  setDatePreset("30d"); 
                  setDateRange({ from: subDays(new Date(), 30), to: new Date() }); 
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  datePreset === "30d" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
              >
                Last 30 Days
              </button>
              <button
                onClick={() => { 
                  setDatePreset("thisMonth"); 
                  setDateRange({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }); 
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  datePreset === "thisMonth" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
              >
                This Month
              </button>
              <button
                onClick={() => { 
                  const lastMonth = subMonths(new Date(), 1);
                  setDatePreset("lastMonth"); 
                  setDateRange({ from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }); 
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  datePreset === "lastMonth" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                }`}
              >
                Last Month
              </button>
              
              {/* Custom Date Range Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                      datePreset === "custom" ? 'bg-hsl-primary text-black' : 'bg-hsl-surface text-zinc-400 hover:text-white'
                    }`}
                  >
                    <CalendarIcon size={14} />
                    {datePreset === "custom" && dateRange.from ? (
                      dateRange.to ? (
                        `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                      ) : format(dateRange.from, "MMM d, yyyy")
                    ) : "Custom"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-hsl-surface border-white/10" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDatePreset("custom");
                      setDateRange(range || { from: null, to: null });
                    }}
                    numberOfMonths={2}
                    className="text-white"
                  />
                  <div className="p-3 border-t border-white/10 flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setDatePreset("all"); setDateRange({ from: null, to: null }); }}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Sport Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
              {/* Favorites Quick Filter - only show if user has favorites */}
              {user && user.favorite_sports && user.favorite_sports.length > 0 && (
                <button
                  onClick={() => setSelectedSport("favorites")}
                  className={`sport-filter-btn flex-shrink-0 flex items-center gap-1 ${selectedSport === "favorites" ? 'active' : ''}`}
                  data-testid="filter-favorites"
                  title="Show only your favorite sports"
                >
                  <Star size={14} className={selectedSport === "favorites" ? "fill-black" : "fill-yellow-500 text-yellow-500"} />
                  My Favorites
                </button>
              )}
              {SPORTS.map((sport) => (
                <button
                  key={sport}
                  onClick={() => setSelectedSport(sport)}
                  className={`sport-filter-btn flex-shrink-0 flex items-center gap-1 ${selectedSport === sport ? 'active' : ''}`}
                  data-testid={`filter-${sport.toLowerCase()}`}
                >
                  {sport}
                  {/* Star icon to add/remove from favorites */}
                  {user && sport !== "All" && (
                    <Star 
                      size={12} 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(sport);
                      }}
                      className={`cursor-pointer transition-colors ${
                        user.favorite_sports?.includes(sport) 
                          ? "fill-yellow-500 text-yellow-500" 
                          : "text-zinc-600 hover:text-yellow-500"
                      }`}
                    />
                  )}
                </button>
              ))}
              
              {/* Manual Refresh Button */}
              <button
                onClick={refreshPicks}
                disabled={refreshing}
                className="ml-2 flex items-center gap-1.5 px-3 py-2 bg-hsl-surface border border-white/10 rounded-lg text-zinc-400 hover:text-hsl-primary hover:border-hsl-primary/50 transition-colors text-sm font-medium disabled:opacity-50"
                title="Refresh scores"
                data-testid="refresh-scores-btn"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show signup prompt for non-logged in users viewing active picks */}
        {!user && statusFilter === "active" && (
          <div className="bg-gradient-to-br from-hsl-primary/20 via-hsl-surface to-hsl-surface border border-hsl-primary/40 rounded-2xl p-8 mb-8 text-center" data-testid="active-picks-gate">
            <Lock className="w-16 h-16 text-hsl-primary mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-white uppercase mb-2">
              Get Today's Picks Free
            </h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Create a free account to unlock all active picks and analysis. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
                className="btn-primary-glow px-8 py-3 text-lg font-heading font-bold uppercase"
                data-testid="signup-gate-btn"
              >
                Sign Up Free
              </Button>
              <Button 
                variant="outline"
                onClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
                className="border-white/20 text-white hover:bg-white/10 px-8 py-3"
                data-testid="login-gate-btn"
              >
                Already have an account? Log In
              </Button>
            </div>
            <p className="text-zinc-500 text-sm mt-4">
              Want to see our track record first? <button onClick={() => setStatusFilter("completed")} className="text-hsl-primary hover:underline">View completed picks →</button>
            </p>
          </div>
        )}

        {!user && statusFilter !== "active" && (
          <div className="bg-hsl-surface border border-hsl-primary/30 rounded-xl p-6 mb-8 flex items-center gap-4" data-testid="login-banner">
            <AlertCircle className="text-hsl-primary w-6 h-6 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white font-semibold">Sign up to see today's active picks</p>
              <p className="text-zinc-400 text-sm">Create a free account to unlock all picks and analysis</p>
            </div>
            <Button 
              onClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
              className="btn-primary-glow px-6 py-2 rounded-sm" 
              data-testid="signup-banner-btn"
            >
              Sign Up Free
            </Button>
          </div>
        )}
        
        {/* Notification Toggle for logged-in users */}
        {user && (
          <NotificationToggle />
        )}

        {/* All Picks - Single unified list */}
        <div className="space-y-4">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-xl" />
            ))
          ) : !user && statusFilter === "active" ? (
            // Don't show active picks to non-logged in users - they see the gate above
            null
          ) : picks.length > 0 ? (
            picks.map((pick) => (
              <PickCard key={pick.id} pick={pick} isPremiumUser={isPremiumUser} tagStats={tagStats} />
            ))
          ) : (
            <div className="text-center py-16 bg-hsl-surface rounded-xl border border-white/5" data-testid="no-picks">
              <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 text-lg">No picks found</p>
              <p className="text-zinc-600 text-sm mt-1">Try a different date filter</p>
            </div>
          )}
        </div>
      </div>
      
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default DashboardPage;
